import type {
  DatasetConfig, DatasetConfigMutable, DatasetType,
  Pipe, SubType, MediaImportResponse, PipelineParams,
  VideoSearchIndexMeta, VideoSearchIndexMethod,
} from 'dive-common/apispec';
import { Attribute } from 'vue-media-annotator/use/AttributeTypes';
import { AttributeTrackFilter } from 'vue-media-annotator/AttributeTrackFilterControls';
import { ImageEnhancements } from 'vue-media-annotator/use/useImageEnhancements';
import type { ScoringJobArgs } from 'dive-common/scoring/types';
import type { StitchedSide } from 'vue-media-annotator/stitchedStereo';

export const JsonConfigCurrentVersion = 1;
export const SettingsCurrentVersion = 1;
export const AnnotationsCurrentVersion = 2;
export const ProjectsFolderName = 'DIVE_Projects';
export const JobsFolderName = 'DIVE_Jobs';
export const JobsOutputFolderName = 'DIVE_Jobs_Output';
export const PipelinesFolderName = 'DIVE_Pipelines';
// Basename (without extension) of the saved "most recently used" calibration.
// The stored file keeps the source file's real extension (e.g. last_calibration.npz).
export const LastCalibrationBaseName = 'last_calibration';
// Cross-dataset "shared" color/style overrides, stored once per data directory
// and reused across every sequence when the shared color scope is enabled.
export const GlobalStyleSettingsFileName = 'global_style_settings.json';

export interface Settings {
  // version a schema version
  version: number;
  // viamePath path to viame install
  viamePath: string;
  // dataPath path to a userspace data directory
  dataPath: string;
  // readonly flag
  readonlyMode: boolean;
  // overrides of user-provided settings
  overrides: {
    // externally force the VIAME path
    viamePath?: string;
    // externally force read only mode flag
    readonlyMode?: boolean;
  };
}

// Handles Importing and storing of multi camera data

export interface Camera {
  type: 'image-sequence' | 'video';
  originalBasePath: string;
  originalImageFiles: string[];
  originalVideoFile: string;
  transcodedImageFiles: string[];
  transcodedVideoFile: string;
  transcodedMisalign?: boolean;
  imageListPath?: string;
  /** Stored camera-local metadata attachment path. */
  metadataFile?: string;
  /** Preserved original name of the camera-local metadata attachment. */
  metadataOriginalName?: string;
  /**
   * Stitched stereo: the media (original and transcoded alike) holds both
   * cameras side by side and this camera is the named half of every frame.
   */
  stitchedSide?: StitchedSide;
}

export interface MultiCamDesktop {
  cameras: Record<string, Camera>;
  // Camera names in display order, chosen at import. Display and pipeline
  // camera ordering both read this; the EO-first/IR-last name heuristic is
  // only a fallback for datasets imported before the order was persisted.
  cameraOrder?: string[];
  //Calibration file in .npz format used for stereo or other cameras
  calibration?: string;
  // Name of the user's original calibration file (preserved for display, since
  // `calibration` may point at a converted/normalized copy).
  calibrationOriginalName?: string;
  // Absolute path of the calibration file at import (before project copy/conversion).
  calibrationSourcePath?: string;
  // Default Display Key for showing multiCam
  defaultDisplay: string;
}

/**
 * JsonConfig is a SUBSET of DatasetConfig contained within
 * the JsonFileSchema.  The remaining parts of DatasetConfig must
 * be generated at load time.
 */
export interface JsonConfig extends DatasetConfigMutable {
  // version used to manage schema migrations
  version: number;

  // immutable dataset type
  type: DatasetType;

  // immutable datset identifier
  id: string;

  // this will become mutable in the future.
  fps: number;

  // the true original video framerate
  originalFps: number;

  // the original name derived from media path
  name: string;

  // the import time of the dataset
  createdAt: string;

  // absolute base path on disk where dataset was imported from.
  // If data was imported from image list, originalBasePath is '' (empty string)
  originalBasePath: string;

  // video file path
  // relative to originalBasePath
  originalVideoFile: string;

  // large image (e.g. GeoTIFF) file path, relative to originalBasePath
  originalLargeImageFile?: string;

  // output of web safe transcoding
  // relative to project path
  transcodedVideoFile: string;

  transcodedMisalign?: boolean;

  // ordered image filenames IF this is an image dataset
  // If paths are relative, they're relative to originalBasePath
  // If paths are absolute, originalBasePath will be '' (empty string)
  originalImageFiles: string[];

  // ordered image filenames of transcoded images
  // relative to project path
  transcodedImageFiles: string[];

  // manifest source path IF image list was used.
  imageListPath?: string;

  // If the dataset required transcoding, specify the job
  // key that ran transcoding
  transcodingJobKey?: string;

  error?: string;

  // attributes are not DatasetConfigMutable and are stored separate
  attributes?: Record<string, Attribute>;

  // attributes are not DatasetConfigMutable and are stored separate
  attributeTrackFilters?: Record<string, AttributeTrackFilter>;

  // confidence filter threshold for exporting
  confidenceFilters?: Record<string, number>;

  // image enhancement settings
  imageEnhancement?: ImageEnhancements;

  // Stereo or multi-camera datasets with uniform type (all images, all video)
  multiCam: MultiCamDesktop | null;

  // Stereo or multi-camera datasets with uniform type (all images, all video)
  subType: SubType;

  // Optional per-dataset metadata file (e.g. sea-lion flight log), copied into
  // the project directory at import. Absolute path to the stored copy; handed to
  // opt-in pipelines at run time (see the pipe `# Metadata File:` header).
  metadataFile?: string;
  // The user's original metadata file name, preserved for display.
  metadataOriginalName?: string;

  // Per-camera dataset of a stitched stereo import; see Camera.stitchedSide.
  stitchedSide?: StitchedSide;

  // execTime is athe execution time for desktop runs
  execTime?: number
}

export type DesktopConfig = DatasetConfig & JsonConfig;

interface NvidiaSmiTextRecord {
  _text: string;
}

export interface NvidiaSmiReply {
  // output condensed output from nvidia smi xml converted to json
  output: {
    nvidia_smi_log: {
      driver_version: NvidiaSmiTextRecord;
      cuda_version: NvidiaSmiTextRecord;
      attached_gpus: NvidiaSmiTextRecord;
    };
  } | null;
  // process exit code
  exitCode: number | null;
  // error message
  error: string;
}

export enum JobType {
  Conversion,
  ExportTrainedPipeline,
  RunPipeline,
  RunTraining,
  RunScoring,
  BuildSearchIndex,
}

export interface JobArgs {
  type: JobType;
}

/** TODO promote to apispec */
export interface RunPipeline extends JobArgs {
  type: JobType.RunPipeline;
  datasetId: string;
  pipeline: Pipe;
  pipelineParams?: PipelineParams;
  outputDatasetName?: string;
}

export interface ExportTrainedPipeline extends JobArgs {
  type: JobType.ExportTrainedPipeline;
  path: string;
  pipeline: Pipe;
}

/** TODO promote to apispec */
export interface RunTraining extends JobArgs {
  type: JobType.RunTraining;
  // datasets to run training on
  datasetIds: string[];
  // new pipeline name to be created
  pipelineName: string;
  // training configuration file name
  trainingConfig: string;
  // train only on annotated frames
  annotatedFramesOnly: boolean;
  // contents of labels.txt file
  labelText?: string;
  // fine tuning model
  fineTuneModel?: {
    name: string;
    type: string;
    path?: string;
    folderId?: string;
  };
  // working directory of a prior interrupted run to continue from
  resumeWorkingDir?: string;
}

export interface RunScoring extends JobArgs, ScoringJobArgs {
  type: JobType.RunScoring;
}

export interface ConversionArgs extends JobArgs {
  type: JobType.Conversion;
  meta: JsonConfig;
  mediaList: [string, string][];
  importWarnings?: string[];
}

/** IPC payload when a CLI open must wait on media conversion. */
export interface CliTranscodingNotice {
  datasetId: string;
  name: string;
  mediaCount: number;
}

/** Build a video search / IQR descriptor index over a dataset. */
export interface BuildSearchIndex extends JobArgs {
  type: JobType.BuildSearchIndex;
  datasetId: string;
  // detections: index around generic object proposals
  // tracking: index around tracked proposals
  // existing: index around this dataset's existing annotations
  method: VideoSearchIndexMethod;
}

/** Sidecar metadata written alongside a built search index. */
export type SearchIndexMeta = VideoSearchIndexMeta;

export type Job = ConversionArgs | RunPipeline | RunTraining
  | ExportTrainedPipeline | RunScoring | BuildSearchIndex;

export interface DesktopJob {
  // key unique identifier for this job
  key: string;
  // command that was run
  command: string;
  // jobType identify type of job
  jobType: 'pipeline' | 'training' | 'conversion' | 'export' | 'scoring' | 'indexing';
  // title whatever humans should see this job called
  title: string;
  // arguments to creation
  args: RunPipeline | RunTraining | ExportTrainedPipeline | ConversionArgs | RunScoring
    | BuildSearchIndex;
  // datasetIds of the involved datasets
  datasetIds: string[];
  // pid of the process spawned
  pid: number;
  // workingDir of the job's output
  workingDir: string;
  // exitCode if set, the job exited already
  exitCode: number | null;
  // startTime time of process initialization
  startTime: Date;
  // endTime time of process exit
  endTime?: Date;
  // cancelledJob
  cancelledJob?: boolean;
}

export interface DesktopMediaImportResponse extends MediaImportResponse {
  jsonConfig: JsonConfig;
  trackFileAbsPath: string;
  multiCamTrackFiles: null | Record<string, string>;
  forceMediaTranscode: boolean;
  /** Absolute path of an optional DIVE Configuration File (JSON) chosen at import. */
  configFileAbsPath?: string;
  /** Absolute path of an optional KWCOCO species list chosen at import. */
  speciesFileAbsPath?: string;
  /** Absolute path of an optional Metadata File (pipeline sidecar) chosen at import. */
  // Absolute path of the one optional metadata attachment. Seeded by import-time discovery
  // (an exported folder's metadata/ directory, or a reserved-name file beside the media) and
  // shown in the import dialog, where the user can clear or replace it before finalize.
  metadataFileAbsPath?: string;
  // Non-fatal problems found while preparing the import (e.g. a registration
  // file naming cameras this dataset doesn't have), shown in the import dialog.
  importWarnings?: string[];
}

export type {
  MultiCamBatchCamera,
  MultiCamBatchCollect,
  MultiCamBatchScanResult,
} from 'dive-common/multiCamBatchScan';

export interface DesktopJobUpdate extends DesktopJob {
  // body contents of update payload
  body: string[];
}

export type DesktopJobUpdater = (msg: DesktopJobUpdate) => void;

interface FFProbeFrame {
  best_effort_timestamp_time: number;
}
export interface FFProbeFrameResults{
  frames: FFProbeFrame[];
}

export interface ExportDatasetArgs {
    id: string;
    exclude: boolean;
    path: string;
    typeFilter: Set<string>;
    type?: 'csv' | 'json' | 'coco';
  }

export interface ExportConfigurationArgs {
    id: string;
   path: string;
}

export interface ExportMulticamEverythingArgs {
  id: string;
  exclude: boolean;
  path: string;
  typeFilter: Set<string>;
}
